import { PlyParser } from '../parsers/plyParser';
import { ObjParser } from '../parsers/objParser';
import { StlParser } from '../parsers/stlParser';
import { describeLayout, parsePcdWasm, parsePtsWasm, parseXyzWasm, toPcdPayload, toPointCloudPayload, } from '../parsers/pointcloudWasm';
import { KittiBinParser } from '../parsers/kittiBinParser';
import { StonexX3aParser } from '../parsers/stonexX3aParser';
import { OffParser } from '../parsers/offParser';
import { GltfParser } from '../parsers/gltfParser';
import { NpyParser } from '../parsers/npyParser';
import { inspectNpyWasm, isNpyPointCloudShape } from '../parsers/pointcloudWasm';
import { NrrdParser } from '../parsers/nrrdParser';
import { sampleRange } from '../visualization/isosurface';
import { buildVolumePoints } from '../visualization/volumePoints';
import { defaultVolumeBrightnessMode, resolveVolumeWindow, volumeSliceRanges, } from '../visualization/volumePresentation';
import { initTiffWasm, projectCameraPointsWasmSync } from '../depth/readers/tiffWasm';
/**
 * Registration of every format the viewer accepts.
 *
 * Each entry is the whole story for one format: which extensions it claims,
 * which pipeline it belongs to, and how to decode it. Adding a format means
 * adding one `register` call here and nothing else.
 *
 * `convertToUnifiedFormat` arrives as an argument rather than an import
 * because it lives in fileHandler.ts, which imports this module - taking it as
 * a dependency keeps that one-directional instead of a cycle.
 */
/**
 * Also used directly by fileHandler's multi-file path: an X3A archive can hold
 * several scans, and `parseAll` turns each into its own entry.
 */
export async function createStonexParser() {
    const ready = await initTiffWasm();
    return new StonexX3aParser(ready
        ? request => projectCameraPointsWasmSync({
            ...request,
            cameraModel: 'pinhole-opencv',
            coefficients: [...request.coefficients],
        })
        : undefined);
}
const registered = new WeakSet();
export function registerBuiltinFormats(registry, convertToUnifiedFormat) {
    // `register` throws on a duplicate extension, which is the right behaviour
    // for a genuine mistake but would be a module-load crash if this ever ran
    // twice against the same registry (two entry bundles sharing the module, a
    // dev-server reload). Registering once per registry keeps that a no-op.
    if (registered.has(registry)) {
        return;
    }
    registered.add(registry);
    registry.register({
        extensions: ['ply'],
        category: 'pointCloud',
        async parse({ data, fileName, timingCallback }) {
            const spatialData = await new PlyParser().parse(data, timingCallback);
            return {
                data: {
                    ...spatialData,
                    fileName,
                    // Splat mode needs the full original PLY (SH/scale/rot props are
                    // dropped during point parsing). Only 3DGS files pay this retention.
                    ...(spatialData.isGaussianSplat ? { splatSource: { bytes: data } } : {}),
                },
                type: 'spatialData',
            };
        },
    });
    registry.register({
        extensions: ['xyz', 'xyzn', 'xyzrgb'],
        category: 'pointCloud',
        async parse({ data, fileName, timingCallback }) {
            // The variant decides the column layout, and only the file name carries
            // it - `.xyzn` is normals, `.xyzrgb` colours, plain `.xyz` auto-detects.
            const variant = fileName.split('.').pop()?.toLowerCase() ?? 'xyz';
            timingCallback?.(`🔍 XYZ: parsing ${variant} in Rust...`);
            const parsed = await parseXyzWasm(data, variant);
            timingCallback?.(`✅ XYZ: parsed ${parsed.vertexCount.toLocaleString()} points`);
            const payload = toPointCloudPayload(parsed, 'ascii', [
                `Converted from ${variant.toUpperCase()}: ${fileName}`,
                `Format variant: ${variant}`,
            ]);
            return { data: convertToUnifiedFormat(payload, fileName), type: 'xyzData' };
        },
    });
    registry.register({
        extensions: ['pcd'],
        category: 'pointCloud',
        async parse({ data, fileName, timingCallback }) {
            timingCallback?.('🔍 PCD: parsing in Rust...');
            const parsed = await parsePcdWasm(data);
            timingCallback?.(`✅ PCD: parsed ${parsed.vertexCount.toLocaleString()} points (${parsed.header.format}, ` +
                `fields: ${parsed.header.fields.join(' ')})`);
            return { data: convertToUnifiedFormat(toPcdPayload(parsed), fileName), type: 'pcdData' };
        },
    });
    registry.register({
        extensions: ['pts'],
        category: 'pointCloud',
        async parse({ data, fileName, timingCallback }) {
            timingCallback?.('🔍 PTS: parsing in Rust...');
            const parsed = await parsePtsWasm(data);
            timingCallback?.(`✅ PTS: parsed ${parsed.vertexCount.toLocaleString()} points (${describeLayout(parsed)})`);
            return {
                data: convertToUnifiedFormat(toPointCloudPayload(parsed, 'pts'), fileName),
                type: 'ptsData',
            };
        },
    });
    registry.register({
        // KITTI velodyne scans.
        extensions: ['bin'],
        category: 'pointCloud',
        async parse({ data, fileName, timingCallback }) {
            const kittiBinData = await new KittiBinParser().parse(data, timingCallback);
            return { data: convertToUnifiedFormat(kittiBinData, fileName), type: 'kittiBinData' };
        },
    });
    registry.register({
        extensions: ['x3a', 'x3r'],
        category: 'pointCloud',
        async parse({ data, fileName, timingCallback }) {
            const parser = await createStonexParser();
            return { data: await parser.parse(data, fileName, timingCallback), type: 'spatialData' };
        },
    });
    registry.register({
        // Decoded by the lidar worker (parsers/lidarWorker.ts), which is chosen
        // before parseFileData is reached; registered so the files are accepted.
        extensions: ['las', 'laz', 'e57'],
        category: 'pointCloud',
    });
    registry.register({
        // Gaussian splat containers, decoded by Spark (visualization/splatMode.ts).
        extensions: ['spz', 'splat', 'ksplat', 'sog'],
        category: 'pointCloud',
    });
    registry.register({
        extensions: ['stl'],
        category: 'mesh',
        async parse({ data, fileName, timingCallback }) {
            const stlData = await new StlParser().parse(data, timingCallback);
            return { data: convertToUnifiedFormat(stlData, fileName), type: 'stlData' };
        },
    });
    registry.register({
        extensions: ['obj'],
        category: 'mesh',
        async parse({ data, fileName, timingCallback }) {
            const objData = await new ObjParser().parse(data, timingCallback);
            return { data: convertToUnifiedFormat(objData, fileName), type: 'objData' };
        },
    });
    registry.register({
        extensions: ['off'],
        category: 'mesh',
        async parse({ data, fileName, timingCallback }) {
            const offData = await new OffParser().parse(data, timingCallback);
            return { data: convertToUnifiedFormat(offData, fileName), type: 'offData' };
        },
    });
    registry.register({
        extensions: ['fbx', 'dae', '3ds', '3mf', 'amf', 'wrl', 'step', 'stp', 'iges', 'igs', 'brep'],
        category: 'mesh',
    });
    registry.register({
        extensions: ['gltf', 'glb'],
        category: 'mesh',
        async parse({ data, fileName, timingCallback }) {
            const gltfData = await new GltfParser().parse(data, timingCallback);
            return { data: convertToUnifiedFormat(gltfData, fileName), type: 'gltfData' };
        },
    });
    registry.register({
        // Volumes. NRRD is the payload the tiff-visualizer bridge hands over for
        // DICOM/OME-TIFF stacks, and the format 3D Slicer and ITK write, so the
        // same path serves both. The canonical representation is one point per
        // retained voxel, coloured with the source presentation window.
        extensions: ['nrrd', 'nhdr'],
        category: 'mesh',
        async parse({ data, fileName, timingCallback }) {
            const volume = await new NrrdParser().parse(data, fileName, timingCallback);
            const range = sampleRange(volume);
            const window = resolveVolumeWindow(volume, range);
            const { data: pointData } = buildVolumePoints(volume, {
                threshold: range.min,
                step: [1, 1, 1],
                windowCenter: window.center,
                windowWidth: window.width,
                brightnessMode: defaultVolumeBrightnessMode(volume),
                volumeRange: range,
                sliceRanges: volumeSliceRanges(volume),
                onProgress: fraction => timingCallback?.(`🧊 Volume points: ${(fraction * 100).toFixed(0)}%`),
            });
            return { data: { ...pointData, fileName }, type: 'spatialData' };
        },
    });
    registry.register({
        // Depth formats are projected by DepthRegistry, not parsed here.
        extensions: ['tif', 'tiff', 'pfm', 'npz', 'png', 'exr'],
        category: 'depthImage',
    });
    registry.register({
        // NPY holds either a depth image or an XYZ point cloud; only the array
        // shape tells them apart, hence refineCategory.
        extensions: ['npy'],
        category: 'depthImage',
        async refineCategory(data) {
            try {
                const [array] = await inspectNpyWasm(data);
                return array && isNpyPointCloudShape(array.shape) ? 'pointCloud' : null;
            }
            catch (error) {
                // Unreadable header: keep the depth-image assumption, as before.
                console.warn('Failed to analyze NPY content, treating as depth image:', error);
                return null;
            }
        },
        async parse({ data, fileName, category, timingCallback }) {
            if (category !== 'pointCloud') {
                throw new Error(`NPY file ${fileName} was not detected as point cloud data. Shape may not end with dimension 3.`);
            }
            const npyData = await new NpyParser().parse(data, timingCallback);
            return { data: { ...npyData, fileName }, type: 'npyData' };
        },
    });
    registry.register({
        extensions: ['json'],
        category: 'poseData',
        async parse({ data, fileName }) {
            // Pose data and camera profiles. The real parsing happens in main.ts
            // based on content structure; the JSON text rides along in `comments`.
            const textContent = new TextDecoder().decode(data);
            return {
                data: {
                    vertices: [],
                    faces: [],
                    format: 'json',
                    vertexCount: 0,
                    faceCount: 0,
                    hasColors: false,
                    hasNormals: false,
                    fileName,
                    comments: [textContent],
                },
                type: 'jsonData',
            };
        },
    });
}
